import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {DateCell} from '../../components/common/table/cells/dateCell';
import {TableCellProps} from '../../components/common/table/cells/tableCell';
import {TeammateCell} from '../../components/common/table/cells/teammateCell';
import {TextCell} from '../../components/common/table/cells/textCell';
import {TableRow, TableRowRenderOptions} from '../../components/common/table/tableRow';
import {TeammateModel} from '../../models/api/teammateModel';
import {
  circleEditorTeammateColumns,
  CircleEditorTeammateColumnsEnum,
} from './circleEditorTeammateTableConfig';

/*
 * Types.
 */

interface CellProps extends TableCellProps<number> {
  shouldRemoveTopBorderRadius: boolean;
  shouldRemoveBottomBorderRadius: boolean;
  isInMultiSelectMode: boolean;
  addedAt: number | undefined;
}

type CellRender = (teammate: TeammateModel, cellProps: CellProps) => ReactNode;

/*
 * Props.
 */

interface CircleEditorTeammateTableRowProps {
  teammate: TeammateModel;
  options: TableRowRenderOptions;
  isSelected: boolean;
  onSelect: Handler;
  shouldRemoveBottomBorderRadius: boolean;
  shouldRemoveTopBorderRadius: boolean;
  isInMultiSelectMode: boolean;
  // addedAt is undefined when a teammate is added to a circle and this circle is not yet saved.
  addedAt: number | undefined;
}

/*
 * Style.
 */

const StyledTableRow = styled(TableRow)<{$minHeight: number}>`
  min-height: ${(p) => p.$minHeight}px;
`;

const StyledTeammateCell = styled(TeammateCell)`
  font-weight: ${fontWeights.semibold};
`;

/*
 * Component.
 */

export const CircleEditorTeammateTableRow: FC<CircleEditorTeammateTableRowProps> = (props) => {
  const {
    teammate,
    isSelected,
    options,
    shouldRemoveBottomBorderRadius,
    shouldRemoveTopBorderRadius,
    isInMultiSelectMode,
    addedAt,
    onSelect,
  } = props;

  return (
    <StyledTableRow
      $minHeight={options.tableRowHeight}
      hasHover
      renderColumn={(isHovering) =>
        circleEditorTeammateColumns.map((column, index) => (
          <React.Fragment key={column.id}>
            {columnRender[column.type](teammate, {
              ...options,
              index,
              isHovering,
              isSelected,
              settings: column.settings,
              shouldRemoveBottomBorderRadius,
              shouldRemoveTopBorderRadius,
              isInMultiSelectMode,
              addedAt,
            })}
          </React.Fragment>
        ))
      }
      shouldAllowMultiSelect={options.shouldAllowMultiSelect}
      shouldShowRightGradient={options.shouldShowRightGradient}
      isSelected={isSelected}
      onSelect={onSelect}
      isCheckboxesVisible={isInMultiSelectMode}
    />
  );
};

/*
 * Helpers.
 */

const columnRender: Record<CircleEditorTeammateColumnsEnum, CellRender> = {
  [CircleEditorTeammateColumnsEnum.NAME]: (teammate, cellProps) => (
    <StyledTeammateCell {...cellProps} value={teammate} avatarSize={VisualSizesEnum.MEDIUM} />
  ),
  [CircleEditorTeammateColumnsEnum.EMAIL]: (teammate, cellProps) => (
    <TextCell {...cellProps} value={teammate.email} />
  ),
  [CircleEditorTeammateColumnsEnum.JOINED]: (teammate, cellProps) => (
    <DateCell
      {...cellProps}
      value={cellProps.addedAt}
      customFormat={{day: '2-digit', month: 'short', year: 'numeric'}}
    />
  ),
};
